import { InvalidArticleError } from '../errors/InvalidArticleError';

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * One feature covers both اخبار (news) and مقاله‌ها (articles) rather than
 * duplicating the entity, mirroring the mineralogy/petrology precedent in
 * features/scientific-content — the two only differ by this field and by
 * where they show up in navigation; the editorial shape (summary, body,
 * cover image, draft/publish) is identical.
 */
export type ArticleCategory = 'news' | 'article';
const VALID_CATEGORIES: ArticleCategory[] = ['news', 'article'];

export type ArticleStatus = 'draft' | 'published';
const VALID_STATUSES: ArticleStatus[] = ['draft', 'published'];

export interface ArticleInput {
  id: string;
  slug: string;
  title: string;
  category: ArticleCategory;
  summary: string;
  body: string;
  coverImage: string | null;
  /** ISO date string, or null for a draft not yet scheduled/published. */
  publishedAt: string | null;
  status: ArticleStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * Entity per the pattern in features/scientific-content/domain/entities/ScientificEntry.entity.ts.
 *
 * Real business rules:
 *  - A `draft` may be incomplete — an author can start a piece and come back
 *    to it later.
 *  - A `published` article must be usable content: a summary, a body, and a
 *    cover image are required. Publishing an empty page to the public site
 *    is a content-entry mistake, not a valid state.
 */
export class Article {
  private constructor(public readonly props: ArticleInput) {
    Object.freeze(this.props);
    Object.freeze(this);
  }

  public static create(input: ArticleInput): Article {
    const title = input.title.trim();
    const slug = input.slug.trim();
    const summary = input.summary.trim();
    const body = input.body.trim();

    if (title.length < 2 || title.length > 200) {
      throw new InvalidArticleError('title must be between 2 and 200 characters.');
    }
    if (!SLUG_PATTERN.test(slug)) {
      throw new InvalidArticleError(`slug "${slug}" must be lowercase kebab-case.`);
    }
    if (!VALID_CATEGORIES.includes(input.category)) {
      throw new InvalidArticleError(`category must be one of: ${VALID_CATEGORIES.join(', ')}.`);
    }
    if (!VALID_STATUSES.includes(input.status)) {
      throw new InvalidArticleError(`status must be one of: ${VALID_STATUSES.join(', ')}.`);
    }
    if (input.publishedAt !== null && Number.isNaN(Date.parse(input.publishedAt))) {
      throw new InvalidArticleError('publishedAt, when set, must be a valid date.');
    }

    if (input.status === 'published') {
      if (summary.length === 0) {
        throw new InvalidArticleError('a published article must have a summary.');
      }
      if (body.length === 0) {
        throw new InvalidArticleError('a published article must have a body.');
      }
      if (!input.coverImage || input.coverImage.trim().length === 0) {
        throw new InvalidArticleError('a published article must have a cover image.');
      }
    }

    return new Article({ ...input, title, slug, summary, body });
  }
}
